import { useState } from "react";

import type { AiSessionFeedbackReason } from "@simplyk/common";

export interface AssistantFeedbackAnswer {
  rating: number | null;
  comment: string | null;
  reason: AiSessionFeedbackReason | null;
}

interface RateInput {
  rating: number;
  comment?: string;
  reason?: AiSessionFeedbackReason;
}

export interface AssistantFeedbackMutations {
  rate: {
    mutateAsync: (input: { aiSessionId: string } & RateInput) => Promise<{ data: AssistantFeedbackAnswer | null }>;
    isPending: boolean;
    variables?: RateInput;
  };
  show: (input: { aiSessionId: string }) => Promise<{ data: { status: string } | null }>;
}

interface UseAssistantFeedbackProps {
  mutations: AssistantFeedbackMutations;
  getAiSessionId: () => string | null;
  initialFeedback: AssistantFeedbackAnswer | null;
  isShownInThread: boolean;
  hasAssistantReply: boolean;
}

export const useAssistantFeedback = ({
  mutations,
  getAiSessionId,
  initialFeedback,
  isShownInThread,
  hasAssistantReply,
}: UseAssistantFeedbackProps) => {
  const [answer, setAnswer] = useState<AssistantFeedbackAnswer>({
    rating: initialFeedback?.rating ?? null,
    comment: initialFeedback?.comment ?? null,
    reason: initialFeedback?.reason ?? null,
  });
  const [isShownOnExit, setIsShownOnExit] = useState(false);
  const [hasRejectedAnswer, setHasRejectedAnswer] = useState(false);
  const { rate: rateMutation, show: showFeedback } = mutations;
  const pendingAnswer = rateMutation.isPending ? rateMutation.variables : undefined;
  const displayedAnswer = pendingAnswer
    ? {
        rating: pendingAnswer.rating,
        comment: pendingAnswer.comment ?? answer.comment,
        reason: pendingAnswer.reason ?? answer.reason,
      }
    : answer;

  const save = async (input: RateInput) => {
    const aiSessionId = getAiSessionId();
    if (!aiSessionId) {
      return;
    }
    setHasRejectedAnswer(false);
    const response = await rateMutation.mutateAsync({ aiSessionId, ...input }).catch(() => null);
    if (response?.data) {
      setAnswer({ rating: response.data.rating, comment: response.data.comment, reason: response.data.reason });
    } else {
      setHasRejectedAnswer(true);
    }
  };

  const showOnExit = async (): Promise<boolean> => {
    const aiSessionId = getAiSessionId();
    if (!aiSessionId || !hasAssistantReply || initialFeedback || isShownInThread || isShownOnExit) {
      return false;
    }
    const { data } = await showFeedback({ aiSessionId });
    const isShown = data?.status === "shown";
    setIsShownOnExit(isShown);
    return isShown;
  };

  return {
    rating: displayedAnswer.rating,
    comment: displayedAnswer.comment,
    reason: displayedAnswer.reason,
    isSaving: rateMutation.isPending,
    hasFailed: hasRejectedAnswer,
    rate: (rating: number) => save({ rating }),
    submitComment: (comment: string) =>
      displayedAnswer.rating === null ? undefined : save({ rating: displayedAnswer.rating, comment }),
    selectReason: (reason: AiSessionFeedbackReason) =>
      displayedAnswer.rating === null ? undefined : save({ rating: displayedAnswer.rating, reason }),
    showOnExit,
  };
};
